import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';

import { firstValueFrom } from 'rxjs';
import { environment } from '@fb/environments';
import { FloatingButtonUser } from '@fb/models';
import { parseFloatingButtonUser } from '@fb/helpers/parse-floating-button-user';

import { getRefreshUrl } from './auth-http.helpers';
import {
  AuthSessionError,
  isTransientAuthRefreshError,
  isUnauthorizedStatus,
} from './auth-session.error';

export const AUTH_REFRESH_RETRY_DELAY_MS = 500;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #httpBackend = inject(HttpBackend);
  #refreshClient = new HttpClient(this.#httpBackend);

  #user: FloatingButtonUser | null = null;
  #refreshInFlight?: Promise<void>;

  setUser(user: FloatingButtonUser | string | null | undefined): void {
    this.#user = parseFloatingButtonUser(user);
  }

  getUser(): FloatingButtonUser | null {
    return this.#user;
  }

  getUserEmail(): string {
    return this.#user?.email ?? '';
  }

  async renewSession(): Promise<void> {
    if (environment.useMocks) {
      return;
    }

    if (this.#refreshInFlight) {
      return this.#refreshInFlight;
    }

    this.#refreshInFlight = this.#renewSessionRequest().finally(() => {
      this.#refreshInFlight = undefined;
    });

    return this.#refreshInFlight;
  }

  async #renewSessionRequest(): Promise<void> {
    try {
      await this.#postRefresh();
    } catch (error) {
      if (!isTransientAuthRefreshError(error)) {
        this.#rethrowRefreshFailure(error);
      }

      await this.#delay(AUTH_REFRESH_RETRY_DELAY_MS);

      try {
        await this.#postRefresh();
      } catch (retryError) {
        this.#rethrowRefreshFailure(retryError);
      }
    }
  }

  async #postRefresh(): Promise<void> {
    await firstValueFrom(
      this.#refreshClient.post(
        getRefreshUrl(),
        {},
        { withCredentials: true },
      ),
    );
  }

  #rethrowRefreshFailure(error: unknown): never {
    if (error instanceof AuthSessionError) {
      throw error;
    }

    if (error instanceof HttpErrorResponse && isUnauthorizedStatus(error.status)) {
      throw new AuthSessionError('Auth or refresh token is incorrect', error.status);
    }

    throw error;
  }

  #delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
